import { Injectable } from '@angular/core';
import { CalcConfigService } from './calc-config.service';
import { QuestionsService } from './questions.service';
import { Firestore, doc, setDoc, docData, DocumentReference } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { CalcRules } from '../_models/calcPackage';
import { UnivPrefsService } from './univ-prefs.service';
import { AuthService } from './auth.service';
import { Question, SchoolQuestions } from 'app/_models/question';
import { isAidYearSupported, unsupportedAidYearMessage } from 'app/_shared/aid-year/aid-year.config';

const TESTINGMODE = false;  // Set to true to force rewrite questions when moving forward in aid years

/**
 * AID YEAR CHANGE SERVICE
 * 
 * This service manages the complex process of switching between different academic aid years
 * in the financial aid calculation system. It handles both calculation rules and questions
 * that need to be migrated when transitioning between aid years.
 * 
 * PURPOSE:
 * Financial aid calculations are tied to specific academic years (e.g., 2024-2025 = 2425).
 * When users need to switch between aid years, this service ensures that:
 * - Current draft rules and questions are properly saved
 * - Target aid year data is loaded or created
 * - All related preferences and school settings are updated
 * - Legacy data formats are maintained for backward compatibility
 * 
 * KEY FUNCTIONALITY:
 * 1. RULES MANAGEMENT:
 *    - Saves current draft calculation rules before switching
 *    - Loads existing rules for target aid year or creates new ones
 *    - Updates metadata (aid year, timestamps, user info)
 *    - Supports force rewrite mode for testing (ignores existing data when moving forward)
 * 
 * 2. QUESTIONS MANAGEMENT:
 *    - Migrates question sets between aid years
 *    - Filters out unused questions (only keeps School Specific questions that are actually used)
 *    - Handles special cases like child support question restructuring for 2526
 *    - Applies question modifications for specific aid years
 *    - Supports force rewrite mode for testing (creates fresh questions when moving forward)
 * 
 * 3. DATA PERSISTENCE:
 *    - Saves data to both new calcConfig collection and legacy collections
 *    - Updates school preferences and settings
 *    - Maintains audit trails with timestamps and user information
 * 
 * 4. VALIDATION & SAFETY:
 *    - Validates prerequisites before switching
 *    - Handles edge cases and missing data gracefully
 *    - Prevents unnecessary operations when already on target aid year
 * 
 * 5. TESTING SUPPORT:
 *    - TESTINGMODE flag controls force rewrite behavior
 *    - When enabled and moving forward in aid years, ignores existing data
 *    - Creates fresh rules and questions from current configuration
 *    - Useful for testing aid year transitions and ensuring clean migrations
 * 
 * USAGE:
 * This service is typically called when users select a different aid year in the UI,
 * triggering a complete migration of their calculation configuration to the new year.
 */

@Injectable({
  providedIn: 'root'
})
export class AidYearChangeService {

  constructor(
    private ups: UnivPrefsService,
    private cc: CalcConfigService,
    private qs: QuestionsService,
    private firestore: Firestore,
    private auth: AuthService
  ) { }

  // Aid Year switching logic
  public async switchAidYear(aidYear: number) {
    const schoolId = this.cc.loadedSchoolId;
    if (!schoolId) { return; }

    const draftRules = this.cc.draftRules.value;
    if (!draftRules) { return; }

    const draftAidYear = draftRules.schoolProperties?.aidYear;
    if (!draftAidYear) { return; }

    // If the draft rules are already in this aid year, do nothing
    if (draftAidYear === aidYear) { return; }

    if (!isAidYearSupported(aidYear)) {
      alert(unsupportedAidYearMessage(aidYear));
      return;
    }

    // Save current draft rules before switching
    await this.saveCurrentDraftRules(schoolId, draftAidYear, draftRules);

    // Handle questions switching
    await this.switchAidYearQuestions(aidYear);

    // Switch to target aid year rules
    const aidYearRules = await this.loadAidYearRules(schoolId, aidYear);
    await this.switchToTargetAidYear(schoolId, aidYear, aidYearRules, draftRules);

    // Update preferences and reload
    await this.updateAidYearPreferences(aidYear, schoolId);
    this.cc.loadDraftCalcRules(schoolId, true);
  }

  private async saveCurrentDraftRules(schoolId: string, draftAidYear: number, draftRules: CalcRules): Promise<void> {
    const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/${draftAidYear}`) as DocumentReference<CalcRules>;
    await setDoc(docRef, draftRules);
  }

  private async loadAidYearRules(schoolId: string, aidYear: number): Promise<CalcRules | undefined> {
    const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/${aidYear}`) as DocumentReference<CalcRules>;
    return await firstValueFrom(docData(docRef)) as CalcRules | undefined;
  }

  private async switchToTargetAidYear(schoolId: string, aidYear: number, aidYearRules: CalcRules | undefined, draftRules: CalcRules): Promise<void> {
    // Turn off subscription while we update
    if (this.cc.draftSubscription) {
      this.cc.draftSubscription.unsubscribe();
    }

    // For testing: force rewrite rules when moving forward in aid years
    const currentAidYear = draftRules.schoolProperties?.aidYear;
    const forceWrite = TESTINGMODE && currentAidYear != null && aidYear > currentAidYear;

    if (aidYearRules && !forceWrite) {
      // Switch to existing aid year rules
      console.log(`Loading existing rules for aid year ${aidYear}`);
      const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/draft`) as DocumentReference<CalcRules>;
      await setDoc(docRef, aidYearRules);
    } else {
      // Create new draft rules for this aid year
      console.log(`Creating new rules for aid year ${aidYear}${forceWrite ? ' (forceWrite enabled)' : ''}`);
      await this.createNewDraftRules(schoolId, aidYear, draftRules);
    }
  }

  private async createNewDraftRules(schoolId: string, aidYear: number, draftRules: CalcRules): Promise<void> {
    const schoolProperties = draftRules.schoolProperties;
    if (!schoolProperties) {
      return;
    }
    const lastUpdatedBy = await this.requireUserId();
    const newDraftRules: CalcRules = {
      ...draftRules,
      schoolProperties: {
        ...schoolProperties,
        aidYear
      },
      lastUpdated: new Date(),
      lastUpdatedBy
    };

    const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/draft`) as DocumentReference<CalcRules>;
    await setDoc(docRef, newDraftRules);
  }

  private async updateAidYearPreferences(aidYear: number, schoolId: string): Promise<void> {
    const stringAidYear = `20${aidYear.toString().slice(0, 2)}-20${aidYear.toString().slice(2)}`;
    this.ups.savePreferences({ aidYear: stringAidYear }, true);
    const docRef = doc(this.firestore, `schools/${schoolId}`);
    await setDoc(docRef,
      { schoolProperties: { draftAidYear: aidYear } },
      { merge: true }
    );
  }

  private async switchAidYearQuestions(aidYear: number): Promise<void> {
    console.log('Switching aid year for schoolId', this.qs.loadedSchoolId, 'aidYear', this.qs.loadedAidYear, 'switching to', aidYear);

    const schoolId = this.qs.loadedSchoolId;
    const draftQuestions = this.qs.draftQuestions.value;
    const draftAidYear = this.qs.loadedAidYear;

    if (!schoolId || !draftQuestions.length || draftAidYear == null || draftAidYear === aidYear) {
      return;
    }

    // Save current questions
    await this.saveCurrentQuestions(schoolId, draftAidYear, draftQuestions);

    // For testing: force rewrite questions when moving forward in aid years
    const forceWrite = TESTINGMODE && aidYear > draftAidYear;

    // Load and switch to target aid year questions (skip loading if forceWrite is true)
    const aidYearQuestions = forceWrite ? undefined : await this.loadAidYearQuestions(schoolId, aidYear);
    const { newDraftQuestions } = await this.switchToTargetQuestions(
      schoolId, aidYear, aidYearQuestions, draftQuestions, forceWrite
    );

    // Handle legacy support with the new draft questions
    await this.handleLegacyQuestions(schoolId, aidYear, aidYearQuestions, newDraftQuestions);
  }

  private async saveCurrentQuestions(schoolId: string, draftAidYear: number, draftQuestions: Question[]): Promise<void> {
    const filteredQuestions = this.filterUnusedQuestions(draftQuestions, `saving current aid year ${draftAidYear}`);

    const schoolQuestions: SchoolQuestions = {
      schoolId,
      aidYear: draftAidYear,
      questions: filteredQuestions,
      lastUpdated: new Date(Date.now()),
      lastUpdatedBy: await this.requireUserId()
    };

    const docRef = doc(this.firestore, `calcConfig/${schoolId}/questions/${draftAidYear}`) as DocumentReference<SchoolQuestions>;
    await setDoc(docRef, schoolQuestions);
  }

  private async loadAidYearQuestions(schoolId: string, aidYear: number): Promise<SchoolQuestions | undefined> {
    const docRef = doc(this.firestore, `calcConfig/${schoolId}/questions/${aidYear}`) as DocumentReference<SchoolQuestions>;
    return await firstValueFrom(docData(docRef)) as SchoolQuestions | undefined;
  }

  private async switchToTargetQuestions(
    schoolId: string,
    aidYear: number,
    aidYearQuestions: SchoolQuestions | undefined,
    draftQuestions: Question[],
    forceWrite: boolean
  ): Promise<{ removedIds: string[], newDraftQuestions: Question[]; }> {
    if (aidYearQuestions) {
      // Switch to existing aid year questions
      const filteredQuestions = this.filterUnusedQuestions(aidYearQuestions.questions, `loading existing aid year ${aidYear}`);
      const docRef = doc(this.firestore, `calcConfig/${schoolId}/questions/draft`) as DocumentReference<SchoolQuestions>;
      await setDoc(docRef, {
        ...aidYearQuestions,
        questions: filteredQuestions
      });
      return { removedIds: [], newDraftQuestions: filteredQuestions };
    }

    // Create new draft questions for this aid year (when no existing questions or forceWrite is enabled)
    console.log(`Creating new questions for aid year ${aidYear}${forceWrite ? ' (forceWrite enabled)' : ''}`);
    return await this.createNewDraftQuestions(schoolId, aidYear, draftQuestions);
  }

  private async createNewDraftQuestions(schoolId: string, aidYear: number, draftQuestions: Question[]): Promise<{ removedIds: string[], newDraftQuestions: Question[]; }> {
    let questions = [...draftQuestions];
    let removedIds: string[] = [];

    // FIRST: Filter out unused questions
    questions = this.filterUnusedQuestions(questions, `filtering before modifications for aid year ${aidYear}`);

    // THEN: Apply modifications (only to questions that are still in the list)
    const result = await this.checkForModifiedQuestions(questions, aidYear);
    questions = result.questions;
    removedIds = result.removedQuestionIds;

    const schoolQuestions: SchoolQuestions = {
      schoolId,
      aidYear,
      questions: questions, // No need to filter again
      lastUpdated: new Date(Date.now()),
      lastUpdatedBy: await this.requireUserId()
    };

    const docRef = doc(this.firestore, `calcConfig/${schoolId}/questions/draft`) as DocumentReference<SchoolQuestions>;
    await setDoc(docRef, schoolQuestions);
    return { removedIds, newDraftQuestions: questions };
  }

  private async handleLegacyQuestions(schoolId: string, aidYear: number, aidYearQuestions: SchoolQuestions | undefined, newDraftQuestions: Question[]): Promise<void> {
    if (this.qs.draftSubscription) {
      this.qs.draftSubscription.unsubscribe();
    }

    // Use existing aid year questions if available, otherwise use the new draft questions
    const finalQuestions = aidYearQuestions ? aidYearQuestions.questions : newDraftQuestions;

    console.log(`Legacy questions handler: aidYear=${aidYear}, aidYearQuestions=${!!aidYearQuestions}, finalQuestions.length=${finalQuestions.length}`);

    const filteredFinalQuestions = this.filterUnusedQuestions(finalQuestions, `legacy questions_draft save for aid year ${aidYear}`);

    console.log(`Saving ${filteredFinalQuestions.length} questions to legacy collection for aid year ${aidYear}`);
    const docRef = doc(this.firestore, `questions_draft/${schoolId}`);
    await setDoc(docRef, { questions: filteredFinalQuestions });
    this.qs.loadQuestions(schoolId, aidYear);
  }

  private async checkForModifiedQuestions(questions: Question[], movingToAidYear: number): Promise<{ questions: Question[], removedQuestionIds: string[]; }> {
    console.log('Checking for modified questions');
    const allQuestions = await this.qs.getAllQuestionsForAidYear(movingToAidYear);
    if (!allQuestions?.length) {
      return { questions, removedQuestionIds: [] };
    }

    const modifiedQuestions = allQuestions.filter(q => q.dateDeleted || q.dateUpdated);
    if (modifiedQuestions.length === 0) {
      return { questions, removedQuestionIds: [] };
    }

    const { updatedQuestions, removedQuestionIds } = this.applyQuestionModifications(questions, modifiedQuestions);

    // Special case for aid year 2526: move child support questions to trigger
    if (movingToAidYear === 2526) {
      const newQuestions = this.moveChildSupportQuestionstoTrigger(updatedQuestions, allQuestions);
      return { questions: newQuestions, removedQuestionIds };
    }

    return { questions: updatedQuestions, removedQuestionIds };
  }

  private applyQuestionModifications(questions: Question[], modifiedQuestions: Question[]): { updatedQuestions: Question[], removedQuestionIds: string[]; } {
    const updatedQuestions = [...questions];
    const removedQuestionIds: string[] = [];

    modifiedQuestions.forEach(newQuestion => {
      const index = updatedQuestions.findIndex(question => question.id === newQuestion.id);
      if (index === -1) { return; }

      if (newQuestion.dateDeleted) {
        updatedQuestions.splice(index, 1);
        removedQuestionIds.push(newQuestion.id);
      } else {
        updatedQuestions[index] = newQuestion;
      }
    });

    return { updatedQuestions, removedQuestionIds };
  }

  // One time shift of child support questions to trigger for aid year 2526;
  private moveChildSupportQuestionstoTrigger(questions: Question[], allQuestions: Question[]): Question[] {
    const parentChildSupportQuestionsExist = ['parent.childSupportReceived', 'parent.childSupportPaidAmount'].some(q => questions.find(question => question.id === q));
    const studentChildSupportQuestionsExist = ['student.childSupportReceived', 'student.childSupportPaidAmount'].some(q => questions.find(question => question.id === q));

    if (!parentChildSupportQuestionsExist && !studentChildSupportQuestionsExist) {
      return questions;
    }

    // Remove existing child support questions
    const questionsCopy = questions.filter(q =>
      !['parent.childSupportReceived', 'parent.childSupportPaidAmount', 'student.childSupportReceived', 'student.childSupportPaidAmount'].includes(q.id)
    );

    // Add parent child support questions if they existed
    if (parentChildSupportQuestionsExist) {
      const parentChildSupportQuestions = ['parent.childSupportTrigger', 'parent.childSupportReceived', 'parent.childSupportPaidAmount'];
      parentChildSupportQuestions.forEach(questionId => {
        const question = allQuestions.find(q => q.id === questionId);
        if (question) questionsCopy.push(question);
      });
    }

    // Add student child support questions if they existed
    if (studentChildSupportQuestionsExist) {
      const studentChildSupportQuestions = ['student.childSupportTrigger', 'student.childSupportReceived', 'student.childSupportPaidAmount'];
      studentChildSupportQuestions.forEach(questionId => {
        const question = allQuestions.find(q => q.id === questionId);
        if (question) questionsCopy.push(question);
      });
    }

    console.log('questions.length', questionsCopy.length);
    return questionsCopy;
  }

  private filterUnusedQuestions(questions: Question[], context: string): Question[] {
    const usedByList = this.cc.usedBy.value;
    const usedQuestionIds = usedByList
      .filter(item => item.entityType === 'Question')
      .map(item => item.entityId);

    // Only filter out unused questions from School Specific (SI) section
    // Keep ALL questions from other sections (ST, DS, HP, PI, PA, SF)
    const filteredQuestions = questions.filter(question =>
      question.section_id !== 'SI' || // Keep all non-SI questions
      usedQuestionIds.includes(question.id) || // Keep used SI questions  
      question.methodRequired // Keep method required SI questions
    );

    console.log(`${context}: Filtered ${filteredQuestions.length} questions out of ${questions.length} total (only removed unused SI questions)`);

    return filteredQuestions;
  }

  // This is called when the aid year is not found in the calculation rules.  It is a fallback.
  public async setDraftAidYearFromPreferences() {
    // Wait for first non null value from draft Preferences
    const draftPrefs = await this.ups.draftPrefs.getValue();
    if (!draftPrefs) { return; }

    const aidYear = draftPrefs.aidYear;
    if (aidYear) {
      const aidYearNumber = parseInt(aidYear.slice(2, 4) + aidYear.slice(7, 9));
      if (!isAidYearSupported(aidYearNumber)) {
        alert(unsupportedAidYearMessage(aidYearNumber));
        return;
      }
      console.log('Updating draft Rules with aidyear found in preferences:', aidYearNumber);
      const draftRules = this.cc.draftRules.value;
      if (!draftRules) {
        alert('No draft rules found - contact support');
        return;
      }
      const draftProperties = draftRules.schoolProperties;
      if (!draftProperties) {
        alert('Draft rules are missing school properties - contact support');
        return;
      }
      draftProperties.aidYear = aidYearNumber;
      this.cc.saveDraftRules(this.cc.loadedDraftSchoolId, { schoolProperties: draftProperties });
    } else {
      alert('No aid year found in preferences');
    }
  }

  private async requireUserId(): Promise<string> {
    const user = await this.auth.currentUser();
    if (!user) {
      throw new Error('Cannot change aid year while signed out');
    }
    return user.uid;
  }
}